import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Tabs,
    Tab,
    TextField,
    FormControlLabel,
    Switch,
    RadioGroup,
    Radio,
    useTheme,
    Stack,
    Button,
    Checkbox,
    Tooltip,
    CircularProgress,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { languageService, Language } from '../../services/language-service';
import { DPOption, dpOptionsService } from '../../services/dpoptions-service';
import { translationService } from '../../services/translation-service';
import { config } from '../../config/config';
import AIIcon from '../../assets/images/Artificial_intelligence.png';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Delete from '@mui/icons-material/Delete';
import Add from '@mui/icons-material/Add';

const DISPLAY_TYPES = [
    { value: 'button', label: 'Button' },
    { value: 'checkbox', label: 'Checkbox' }
];

interface EditDPOptionPanelProps {
    open: boolean;
    onClose: () => void;
    option: DPOption | null;
    onSave?: () => void;
}

interface Translation {
    name: string;
    description: string;
    options: { 
        value: string; 
        description?: string;
        displayOrder: number;
        optionId?: number;
    }[];
}

interface InitialValues {
    name: string;
    description: string;
    displayAs: 'button' | 'checkbox';
    allowMultipleSelection: boolean;
    isMandatory: boolean;
    isActive: boolean;
    translations: Record<string, Translation>;
}

interface DPOptionOption {
    optionId?: number;
    optionValue: string;
    optionDescription: string;
    isDefaultSelected: boolean;
    displayOrder: number;
}

const EditDPOptionPanel: React.FC<EditDPOptionPanelProps> = ({ open, onClose, option, onSave }) => {
    const theme = useTheme();
    const [currentTab, setCurrentTab] = useState(0);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [displayAs, setDisplayAs] = useState<'button' | 'checkbox'>('button');
    const [allowMultipleSelection, setAllowMultipleSelection] = useState(false);
    const [isMandatory, setIsMandatory] = useState(false);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [activeLanguage, setActiveLanguage] = useState('en');
    const [translations, setTranslations] = useState<Record<string, Translation>>({});
    const [originalTranslations, setOriginalTranslations] = useState<Record<string, Translation>>({});
    const [hasChanges, setHasChanges] = useState(false);
    const [initialValues, setInitialValues] = useState<InitialValues | undefined>();
    const [isTranslating, setIsTranslating] = useState(false);
    const [isSavingTranslation, setIsSavingTranslation] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [needsGridRefresh, setNeedsGridRefresh] = useState(false);
    const [options, setOptions] = useState<DPOptionOption[]>([]);
    const [initialOptions, setInitialOptions] = useState<DPOptionOption[]>([]);
    const [optionsError, setOptionsError] = useState(false);
    const [defaultSelectedOption, setDefaultSelectedOption] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [isPublished, setIsPublished] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);

    const DRAWER_WIDTH = theme.customLayout.drawerWidth;
    const HEADER_HEIGHT = theme.customLayout.headerHeight;
    const MEDIUM_GAP = theme.customLayout.mediumGap;


    useEffect(() => {
        languageService.getSupportedLanguages().then(langs => {
            setLanguages(langs);
            if (langs.length > 0) {
                setActiveLanguage(langs[0].code);
            }
        });
    }, []);

    useEffect(() => {
        if (!open || !option || languages.length === 0) return;
        setCurrentTab(0);
        setActiveLanguage(languages[0].code);
        setIsPublished(option.isPublished || false);
        setIsLoading(true);

        // Initialize Details and Choices from grid data immediately
        const initialValues = {
            name: option.name || '',
            description: option.description || '',
            displayAs: (option.displayAs || 'button').toLowerCase() as 'button' | 'checkbox',
            allowMultipleSelection: option.allowMultipleSelection || false,
            isMandatory: option.isMandatory || false,
            isActive: option.isActive || false,
            translations: {} // Will be populated after API call
        };
        setInitialValues(initialValues);

        // Set current state values
        setName(initialValues.name);
        setDescription(initialValues.description);
        setDisplayAs(initialValues.displayAs);
        setAllowMultipleSelection(initialValues.allowMultipleSelection);
        setIsMandatory(initialValues.isMandatory);

        // Set options and initialOptions
        const opts = option.options?.map(opt => ({
            ...opt,
            optionDescription: opt.optionDescription ?? '',
            isDefaultSelected: opt.isDefaultSelected ?? false,
            displayOrder: opt.displayOrder ?? 0,
        })) || [];
        setOptions(opts);
        setInitialOptions(opts);
        const defaultIdx = option.options?.findIndex(opt => opt.isDefaultSelected) ?? -1;
        setDefaultSelectedOption(defaultIdx >= 0 ? defaultIdx : null);

        // Initialize translations with English values
        const initialTranslations: Record<string, Translation> = {};
        languages.forEach(lang => {
            initialTranslations[lang.code] = {
                name: lang.code === 'en' ? (option.name || '') : '',
                description: lang.code === 'en' ? (option.description || '') : '',
                options: option.options?.map(opt => ({
                    value: lang.code === 'en' ? (opt.optionValue || '') : '',
                    description: opt.optionDescription || '',
                    displayOrder: opt.displayOrder || 0,
                    optionId: opt.optionId
                })) || []
            };
        });

        // Fetch translations from API
        const fetchTranslations = async () => {
            try {
                const response = await dpOptionsService.getDPOptionById(option.id);
                const dpOption = response.data;
                
                // Update translations with API data
                const newTranslations: Record<string, Translation> = {};
                languages.forEach(lang => {
                    if (lang.code === 'en') {
                        newTranslations[lang.code] = {
                            name: dpOption.name || '',
                            description: dpOption.description || '',
                            options: dpOption.options.map(opt => ({
                                value: opt.optionValue || '',
                                description: opt.optionDescription || '',
                                displayOrder: opt.displayOrder || 0,
                                optionId: opt.optionId
                            }))
                        };
                    } else {
                        // For other languages, use translations if available
                        const langTranslations = dpOption.translations?.[lang.code];
                        newTranslations[lang.code] = {
                            name: langTranslations?.translatedName || '',
                            description: langTranslations?.translatedDescription || '',
                            options: dpOption.options.map(opt => ({
                                value: opt.translations?.[lang.code]?.translatedOptionValue || '',
                                description: opt.translations?.[lang.code]?.translatedOptionDescription || '',
                                displayOrder: opt.displayOrder || 0,
                                optionId: opt.optionId
                            }))
                        };
                    }
                });

                // Update translations state
                const newOriginalTranslations = JSON.parse(JSON.stringify(newTranslations));
                setTranslations(newTranslations);
                setOriginalTranslations(newOriginalTranslations);

                // Update initialValues with translations
                setInitialValues(prev => ({
                    ...prev!,
                    translations: newTranslations
                }));
            } catch (error) {
                console.error('Error fetching translations:', error);
                // If there's an error, use the initial translations
                const newOriginalTranslations = JSON.parse(JSON.stringify(initialTranslations));
                setTranslations(initialTranslations);
                setOriginalTranslations(newOriginalTranslations);

                // Update initialValues with initial translations
                setInitialValues(prev => ({
                    ...prev!,
                    translations: initialTranslations
                }));
            } finally {
                setIsLoading(false);
            }
        };

        fetchTranslations();
    }, [open, option, languages]);

    useEffect(() => {
        if (!activeLanguage || !translations['en']) return;
        if (!translations[activeLanguage] || !translations[activeLanguage].options) {
            setTranslations(prev => ({
                ...prev,
                [activeLanguage]: {
                    name: prev[activeLanguage]?.name || '',
                    description: prev[activeLanguage]?.description || '',
                    options: prev['en'].options.map(opt => ({ ...opt, value: '' }))
                }
            }));
        }
    }, [activeLanguage, translations]);

    useEffect(() => {
        if (option && option.options) {
            setOptions(option.options.map(opt => ({
                ...opt,
                optionDescription: opt.optionDescription ?? '',
                isDefaultSelected: opt.isDefaultSelected ?? false,
                displayOrder: opt.displayOrder ?? 0,
            })));
            const defaultIdx = option.options.findIndex(opt => opt.isDefaultSelected);
            setDefaultSelectedOption(defaultIdx >= 0 ? defaultIdx : null);
        }
    }, [option]);

    const handleTabChange = (_: any, value: number) => setCurrentTab(value);

    const handleLanguageClick = (code: string) => {
        setActiveLanguage(code);
    };

    const handleTranslationFieldChange = (field: 'name' | 'description', value: string) => {
        setTranslations(prev => ({
            ...prev,
            [activeLanguage]: {
                ...prev[activeLanguage],
                [field]: value,
            },
        }));
    };

    const handleOptionTranslationChange = (index: number, value: string) => {
        setTranslations(prev => ({
            ...prev,
            [activeLanguage]: {
                ...prev[activeLanguage],
                options: prev[activeLanguage].options.map((opt, i) =>
                    i === index ? { ...opt, value } : opt
                )
            },
        }));
    };

    const handleTranslateAI = async () => {
        if (!name && !description) return;
        
        setIsTranslating(true);
        try {
            const fields: { [key: string]: string } = {};
            if (name) fields['name'] = name;
            if (description) fields['description'] = description;
            options.forEach((option, index) => {
                if (option.optionValue) {
                    fields[`option_${index}`] = option.optionValue;
                }
            });

            const result = await translationService.translateBatch(fields, activeLanguage);
            
            const translatedOptions = translations[activeLanguage]?.options.map((option, index) => ({
                ...option,
                value: result[`option_${index}`] || ''
            })) || [];

            setTranslations(prev => ({
                ...prev,
                [activeLanguage]: {
                    name: result['name'] || '',
                    description: result['description'] || '',
                    options: translatedOptions
                },
            }));
        } catch (e) {
            setError('AI translation failed');
        } finally {
            setIsTranslating(false);
        }
    };

    const handleSaveTranslation = async () => {
        if (!option) return;
        setIsSavingTranslation(true);
        try {
            const t = translations[activeLanguage];
            const translationPayload = {
                purposePreferenceId: option.id,
                languageCode: activeLanguage,
                translatedName: t.name,
                translatedDescription: t.description,
                options: options.map((opt, index) => ({
                    optionId: opt.optionId || index,
                    translatedOptionValue: t.options[index].value,
                    translatedOptionDescription: opt.optionDescription ? t.options[index].description : undefined
                }))
            };

            await dpOptionsService.saveDPOptionTranslation(
                option.id,
                activeLanguage,
                t.name,
                t.description,
                translationPayload.options.map(opt => ({
                    optionId: opt.optionId,
                    translatedValue: opt.translatedOptionValue
                }))
            );
            
            setOriginalTranslations(prev => ({
                ...prev,
                [activeLanguage]: { ...t },
            }));
            setNeedsGridRefresh(true);
        } catch (e) {
            setError('Failed to save translation');
        } finally {
            setIsSavingTranslation(false);
        }
    };

    const handlePanelClose = () => {
        if (hasChanges) {
            setShowCloseConfirm(true);
        } else {
            if (needsGridRefresh) {
                onSave?.();
            }
            onClose();
            setNeedsGridRefresh(false);
        }
    };

    const handleConfirmClose = () => {
        setShowCloseConfirm(false);
        if (needsGridRefresh) {
            onSave?.();
        }
        onClose();
        setNeedsGridRefresh(false);
    };

    const handleCancelClose = () => {
        setShowCloseConfirm(false);
    };

    const isTranslationEqual = (a: Translation | undefined, b: Translation | undefined) => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        const eq = (x: any, y: any) => (x ?? '') === (y ?? '');
        if (!eq(a.name, b.name) || !eq(a.description, b.description)) return false;
        if ((a.options?.length || 0) !== (b.options?.length || 0)) return false;
        for (let i = 0; i < (a.options?.length || 0); i++) {
            if (!eq(a.options[i].value, b.options[i].value)) return false;
        }
        return true;
    };

    const isTranslationChanged = (langCode: string) => {
        if (isLoading) return false;
        const t = translations[langCode] || { name: '', description: '', options: [] };
        const o = originalTranslations[langCode] || { name: '', description: '', options: [] };
        if ((t.name || '') !== (o.name || '')) return true;
        if ((t.description || '') !== (o.description || '')) return true;
        if ((t.options?.length || 0) !== (o.options?.length || 0)) return true;
        for (let i = 0; i < (t.options?.length || 0); i++) {
            if ((t.options[i]?.value || '') !== (o.options[i]?.value || '')) return true;
        }
        return false;
    };

    const hasAnyTranslationChanged = () => {
        return languages.some(lang => isTranslationChanged(lang.code));
    };

    const handleAddOption = () => {
        setOptions([...options, {
            optionValue: '',
            optionDescription: '',
            isDefaultSelected: false,
            displayOrder: options.length
        }]);
    };

    const handleRemoveOption = (index: number) => {
        setOptions(options.filter((_, i) => i !== index));
        if (defaultSelectedOption === index) {
            setDefaultSelectedOption(null);
        }
    };

    const handleOptionChange = (index: number, field: keyof DPOptionOption, value: string | boolean) => {
        const newOptions = [...options];
        newOptions[index] = {
            ...newOptions[index],
            [field]: value
        };
        setOptions(newOptions);
    };

    const handleDefaultSelectionChange = (index: number) => {
        if (displayAs === 'checkbox' && allowMultipleSelection) {
            // For multiple selection, toggle the checkbox
            handleOptionChange(index, 'isDefaultSelected', !options[index].isDefaultSelected);
        } else {
            // For single selection, update radio selection
            setDefaultSelectedOption(index);
            const newOptions = options.map((opt, i) => ({
                ...opt,
                isDefaultSelected: i === index
            }));
            setOptions(newOptions);
        }
    };

    const handleMoveOption = (index: number, direction: 'up' | 'down') => {
        const newOptions = [...options];
        if (direction === 'up' && index > 0) {
            [newOptions[index], newOptions[index - 1]] = [newOptions[index - 1], newOptions[index]];
            newOptions[index].displayOrder = index;
            newOptions[index - 1].displayOrder = index - 1;
        } else if (direction === 'down' && index < options.length - 1) {
            [newOptions[index], newOptions[index + 1]] = [newOptions[index + 1], newOptions[index]];
            newOptions[index].displayOrder = index;
            newOptions[index + 1].displayOrder = index + 1;
        }
        setOptions(newOptions);
    };

    // Helper: check if there are unsaved changes in Details or Choices
    const hasDetailsOrChoicesChanged = () => {
        if (!initialValues) return false;
        if (
            name !== initialValues.name ||
            description !== initialValues.description ||
            displayAs !== initialValues.displayAs ||
            allowMultipleSelection !== initialValues.allowMultipleSelection ||
            isMandatory !== initialValues.isMandatory
        ) return true;
        // Compare options
        if (options.length !== initialOptions.length) return true;
        for (let i = 0; i < options.length; i++) {
            const o = options[i];
            const orig = initialOptions[i];
            if (!orig ||
                o.optionValue !== orig.optionValue ||
                (o.optionDescription || '') !== (orig.optionDescription || '') ||
                o.isDefaultSelected !== orig.isDefaultSelected ||
                o.displayOrder !== orig.displayOrder
            ) return true;
        }
        return false;
    };

    // Add two new functions for change detection
    const hasDetailsChanged = () => {
        if (!initialValues || isLoading) return false;
        return (
            name !== initialValues.name ||
            description !== initialValues.description ||
            isMandatory !== initialValues.isMandatory
        );
    };

    const hasChoicesChanged = () => {
        if (!initialValues || isLoading) return false;
        if (
            displayAs !== initialValues.displayAs ||
            allowMultipleSelection !== initialValues.allowMultipleSelection
        ) return true;
        if (options.length !== initialOptions.length) return true;
        for (let i = 0; i < options.length; i++) {
            const o = options[i];
            const orig = initialOptions[i];
            if (!orig ||
                o.optionValue !== orig.optionValue ||
                (o.optionDescription || '') !== (orig.optionDescription || '') ||
                o.isDefaultSelected !== orig.isDefaultSelected ||
                o.displayOrder !== orig.displayOrder
            ) return true;
        }
        return false;
    };

    // Save handler for Details + Choices
    const handleSaveDetailsChoices = async () => {
        if (!option) return;
        setSaving(true);
        setError(null);
        try {
            await dpOptionsService.updateDPOption(option.id, {
                name,
                description,
                isMandatory,
                displayAs: displayAs.toUpperCase() as 'BUTTON' | 'CHECKBOX',
                allowMultipleSelection,
                setADefaultSelection: options.some(opt => opt.isDefaultSelected),
                options: options.map((opt, idx) => ({
                    ...opt,
                    displayOrder: idx
                }))
            });
            setSaveSuccess(true);
            setNeedsGridRefresh(true);

            // Update initial values after successful save
            setInitialValues(prev => ({
                ...prev!,
                name,
                description,
                displayAs,
                allowMultipleSelection,
                isMandatory
            }));

            // Update initial options after successful save
            setInitialOptions([...options]);

            // Update initialOptions in initialValues
            setInitialValues(prev => ({
                ...prev!,
                options: options.map((opt, idx) => ({
                    ...opt,
                    displayOrder: idx
                }))
            }));
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Failed to update preference');
        } finally {
            setSaving(false);
        }
    };

    const handlePublishToggle = async () => {
        if (!option) return;
        if (isPublished) {
            setIsPublishing(true);
            try {
                await dpOptionsService.unpublishDPOption(option.id);
                setIsPublished(false);
                setNeedsGridRefresh(true);
            } catch (error: any) {
                setError(error.message || 'Failed to unpublish');
            } finally {
                setIsPublishing(false);
            }
            return;
        }

        try {
            // First check if ready to publish
            const readinessCheck = await dpOptionsService.checkPublishReadiness(option.id);
            
            if (!readinessCheck.isReady) {
                // Build discrepancy message
                let message = 'The following issues need to be addressed before publishing:\n\n';
                
                if (readinessCheck.discrepancies.missingOptions) {
                    message += '• At least 2 options are required\n';
                }
                
                if (readinessCheck.discrepancies.missingTranslations?.length) {
                    message += '• Missing translations for the following languages:\n';
                    readinessCheck.discrepancies.missingTranslations.forEach(lang => {
                        message += `  - ${lang.languageName} (${lang.languageCode})\n`;
                    });
                }
                
                message += '\nDo you still want to publish?';
                
                if (!window.confirm(message)) {
                    return;
                }
            }

            setIsPublishing(true);
            await dpOptionsService.publishDPOption(option.id);
            setIsPublished(true);
            setNeedsGridRefresh(true);
        } catch (error: any) {
            console.error('Error publishing/unpublishing:', error);
            // Show error message to user
            alert(error.message || 'Failed to publish/unpublish');
        } finally {
            setIsPublishing(false);
        }
    };

    useEffect(() => {
        return () => {
            if (needsGridRefresh) {
                onSave?.();
            }
        };
    }, [needsGridRefresh, onSave]);

    return (
        <Modal
            open={open}
            onClose={handlePanelClose}
            aria-labelledby="edit-dpoption-modal-title"
            sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', zIndex: 1301 }}
        >
            <Box>
                <Box
                    sx={{
                        bgcolor: theme.palette.background.paper,
                        borderRadius: theme.shape.borderRadius,
                        boxShadow: theme.shadows[24],
                        p: 0,
                        width: `calc(100vw - ${DRAWER_WIDTH + 2 * MEDIUM_GAP}px)`,
                        height: `calc(100vh - ${HEADER_HEIGHT + 2 * MEDIUM_GAP}px)`,
                        position: 'absolute',
                        left: `${DRAWER_WIDTH + MEDIUM_GAP}px`,
                        top: `${HEADER_HEIGHT + MEDIUM_GAP}px`,
                        right: `${MEDIUM_GAP}px`,
                        bottom: `${MEDIUM_GAP}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        outline: 'none',
                    }}
                >
                    <Box sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography id="edit-dpoption-modal-title" variant="h6" sx={{ color: theme.palette.text.primary }}>
                                Edit Data Processing Option
                            </Typography>
                            <Chip
                                label={isPublished ? 'Published' : 'Draft'}
                                color={isPublished ? 'success' : 'default'}
                                size="small"
                                sx={{
                                    fontWeight: 600,
                                    ml: 2,
                                    '& .MuiChip-label': {
                                        color: theme.palette.getContrastText(
                                            isPublished ? theme.palette.success.main : theme.palette.grey[500]
                                        ),
                                    },
                                }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handlePublishToggle}
                                disabled={isPublishing}
                                sx={{
                                    bgcolor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        bgcolor: theme.palette.primary.dark,
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: theme.palette.action.disabledBackground,
                                        color: theme.palette.action.disabled,
                                    }
                                }}
                            >
                                {isPublishing ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    isPublished ? 'Unpublish DP Option' : 'Publish DP Option'
                                )}
                            </Button>
                            <IconButton
                                onClick={handlePanelClose}
                                sx={{
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        bgcolor: theme.palette.action.hover,
                                    },
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Tabs
                            value={currentTab}
                            onChange={handleTabChange}
                            sx={{
                                '& .MuiTab-root': {
                                    color: theme.palette.text.secondary,
                                    '&.Mui-selected': {
                                        color: theme.palette.primary.main,
                                    },
                                },
                            }}
                        >
                            <Tab 
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        Details
                                        {hasDetailsChanged() && (
                                            <Typography 
                                                component="span" 
                                                sx={{ 
                                                    color: theme.palette.primary.main,
                                                    opacity: 0.8,
                                                    fontSize: '1.2rem',
                                                    lineHeight: 1,
                                                    ml: 0.5,
                                                    fontWeight: 500
                                                }}
                                            >
                                                •
                                            </Typography>
                                        )}
                                    </Box>
                                }
                            />
                            <Tab 
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        Choices
                                        {hasChoicesChanged() && (
                                            <Typography 
                                                component="span" 
                                                sx={{ 
                                                    color: theme.palette.primary.main,
                                                    opacity: 0.8,
                                                    fontSize: '1.2rem',
                                                    lineHeight: 1,
                                                    ml: 0.5,
                                                    fontWeight: 500
                                                }}
                                            >
                                                •
                                            </Typography>
                                        )}
                                    </Box>
                                }
                            />
                            <Tab 
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        Translations
                                        {hasAnyTranslationChanged() && (
                                            <Typography 
                                                component="span" 
                                                sx={{ 
                                                    color: theme.palette.primary.main,
                                                    opacity: 0.8,
                                                    fontSize: '1.2rem',
                                                    lineHeight: 1,
                                                    ml: 0.5,
                                                    fontWeight: 500
                                                }}
                                            >
                                                •
                                            </Typography>
                                        )}
                                    </Box>
                                }
                            />
                        </Tabs>
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'auto', bgcolor: theme.palette.background.default }}>
                        {currentTab === 0 && (
                            <Box sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <TextField 
                                        label="Name" 
                                        fullWidth 
                                        size="small" 
                                        required 
                                        value={name} 
                                        onChange={e => setName(e.target.value)}
                                        disabled={isPublished}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: theme.palette.background.paper,
                                                '& fieldset': {
                                                    borderColor: theme.palette.divider,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                            },
                                        }}
                                    />
                                    <TextField 
                                        label="Description" 
                                        fullWidth 
                                        size="small" 
                                        multiline 
                                        rows={3} 
                                        value={description} 
                                        onChange={e => setDescription(e.target.value)}
                                        disabled={isPublished}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: theme.palette.background.paper,
                                                '& fieldset': {
                                                    borderColor: theme.palette.divider,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                            },
                                        }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch 
                                                checked={isMandatory} 
                                                onChange={e => setIsMandatory(e.target.checked)}
                                                disabled={isPublished}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: theme.palette.primary.main,
                                                        '& + .MuiSwitch-track': {
                                                            bgcolor: theme.palette.primary.main,
                                                        },
                                                    },
                                                }}
                                            />
                                        }
                                        label="Is Mandatory"
                                        sx={{ color: theme.palette.text.primary }}
                                    />

                                    {/* Danger Zone */}
                                    {!isPublished && (
                                        <Box sx={{ 
                                            mt: 4, 
                                            p: 3, 
                                            border: `1px solid ${theme.palette.error.main}`,
                                            borderRadius: theme.shape.borderRadius,
                                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(239, 83, 80, 0.08)' : 'rgba(211, 47, 47, 0.04)',
                                        }}>
                                            <Typography variant="h6" sx={{ color: theme.palette.error.main, mb: 1 }}>
                                                Danger Zone
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: theme.palette.error.main, mb: 2 }}>
                                                DP Option once deleted cannot be retrieved
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                onClick={async () => {
                                                    if (window.confirm('Are you sure you want to delete this DP Option? This action cannot be undone.')) {
                                                        if (option?.id) {
                                                            try {
                                                                setIsDeleting(true);
                                                                await dpOptionsService.deleteDPOption(option.id);
                                                                onSave?.();
                                                                onClose();
                                                            } catch (error: any) {
                                                                // Check if it's a permission error
                                                                if (error.response?.status === 403) {
                                                                    setError('You do not have permission to delete this DP Option');
                                                                } else if (error.response?.status === 404) {
                                                                    setError('DP Option not found');
                                                                } else {
                                                                    setError(error.response?.data?.message || 'Failed to delete DP Option');
                                                                }
                                                            } finally {
                                                                setIsDeleting(false);
                                                            }
                                                        }
                                                    }
                                                }}
                                                disabled={isDeleting || isPublishing || saving}
                                                sx={{
                                                    bgcolor: theme.palette.error.main,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.error.dark,
                                                    },
                                                }}
                                            >
                                                {isDeleting ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Delete DP Option'
                                                )}
                                            </Button>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        )}
                        {currentTab === 1 && (
                            <Box sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }}>
                                        Edit Options
                                    </Typography>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.primary }}>
                                            Display as
                                        </Typography>
                                        <RadioGroup
                                            value={displayAs}
                                            onChange={(e) => setDisplayAs(e.target.value as 'button' | 'checkbox')}
                                            row
                                        >
                                            <FormControlLabel 
                                                value="button" 
                                                control={<Radio 
                                                    sx={{
                                                        color: theme.palette.text.secondary,
                                                        '&.Mui-checked': {
                                                            color: theme.palette.primary.main,
                                                        },
                                                    }}
                                                    disabled={isPublished}
                                                />} 
                                                label="Buttons" 
                                                sx={{ color: theme.palette.text.primary }}
                                                disabled={isPublished}
                                            />
                                            <FormControlLabel 
                                                value="checkbox" 
                                                control={<Radio 
                                                    sx={{
                                                        color: theme.palette.text.secondary,
                                                        '&.Mui-checked': {
                                                            color: theme.palette.primary.main,
                                                        },
                                                    }}
                                                    disabled={isPublished}
                                                />} 
                                                label="Checkboxes" 
                                                sx={{ color: theme.palette.text.primary }}
                                                disabled={isPublished}
                                            />
                                        </RadioGroup>
                                    </Box>
                                    {displayAs === 'checkbox' && (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={allowMultipleSelection}
                                                    onChange={(e) => setAllowMultipleSelection(e.target.checked)}
                                                    disabled={isPublished}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: theme.palette.primary.main,
                                                            '& + .MuiSwitch-track': {
                                                                bgcolor: theme.palette.primary.main,
                                                            },
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Allow multiple selections"
                                            sx={{ color: theme.palette.text.primary }}
                                        />
                                    )}
                                    <TableContainer sx={{ bgcolor: theme.palette.background.paper }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: theme.palette.background.paper }}>
                                                    <TableCell sx={{ width: '40px', bgcolor: theme.palette.background.paper, color: theme.palette.text.primary, borderBottom: `1px solid ${theme.palette.divider}` }}></TableCell>
                                                    <TableCell sx={{ width: '35%', bgcolor: theme.palette.background.paper, color: theme.palette.text.primary, borderBottom: `1px solid ${theme.palette.divider}` }}>Option Value</TableCell>
                                                    <TableCell sx={{ width: '40%', bgcolor: theme.palette.background.paper, color: theme.palette.text.primary, borderBottom: `1px solid ${theme.palette.divider}` }}>Description</TableCell>
                                                    <TableCell align="center" sx={{ width: '80px', bgcolor: theme.palette.background.paper, color: theme.palette.text.primary, borderBottom: `1px solid ${theme.palette.divider}` }}>Default</TableCell>
                                                    <TableCell sx={{ width: '40px', bgcolor: theme.palette.background.paper, borderBottom: `1px solid ${theme.palette.divider}` }}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {options.map((option, index) => (
                                                    <TableRow key={index} sx={{ bgcolor: theme.palette.background.paper, '&:hover': { bgcolor: theme.palette.action.hover } }}>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={0}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleMoveOption(index, 'up')}
                                                                    disabled={index === 0 || isPublished}
                                                                    sx={{ 
                                                                        color: theme.palette.text.secondary,
                                                                        padding: '2px',
                                                                        minWidth: '20px',
                                                                        width: '20px',
                                                                        height: '20px',
                                                                        bgcolor: 'transparent',
                                                                        '&:hover': { bgcolor: theme.palette.action.hover }
                                                                    }}
                                                                >
                                                                    <KeyboardArrowUp />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleMoveOption(index, 'down')}
                                                                    disabled={index === options.length - 1 || isPublished}
                                                                    sx={{ 
                                                                        color: theme.palette.text.secondary,
                                                                        padding: '2px',
                                                                        minWidth: '20px',
                                                                        width: '20px',
                                                                        height: '20px',
                                                                        bgcolor: 'transparent',
                                                                        '&:hover': { bgcolor: theme.palette.action.hover }
                                                                    }}
                                                                >
                                                                    <KeyboardArrowDown />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                placeholder="Enter option value"
                                                                required
                                                                value={option.optionValue}
                                                                onChange={(e) => {
                                                                    handleOptionChange(index, 'optionValue', e.target.value);
                                                                    setOptionsError(false);
                                                                }}
                                                                error={optionsError && option.optionValue.trim() === ''}
                                                                helperText={optionsError && option.optionValue.trim() === '' ? "Option value is required" : ""}
                                                                disabled={isPublished}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: theme.palette.background.paper,
                                                                        color: theme.palette.text.primary,
                                                                        '& fieldset': {
                                                                            borderColor: theme.palette.divider,
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: theme.palette.primary.main,
                                                                        },
                                                                    },
                                                                    '& .MuiInputLabel-root': {
                                                                        color: theme.palette.text.secondary,
                                                                    },
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                placeholder="Enter description"
                                                                value={option.optionDescription || ''}
                                                                onChange={(e) => handleOptionChange(index, 'optionDescription', e.target.value)}
                                                                disabled={isPublished}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: theme.palette.background.paper,
                                                                        color: theme.palette.text.primary,
                                                                        '& fieldset': {
                                                                            borderColor: theme.palette.divider,
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: theme.palette.primary.main,
                                                                        },
                                                                    },
                                                                    '& .MuiInputLabel-root': {
                                                                        color: theme.palette.text.secondary,
                                                                    },
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {displayAs === 'checkbox' && allowMultipleSelection ? (
                                                                <Checkbox
                                                                    checked={option.isDefaultSelected}
                                                                    onChange={() => handleDefaultSelectionChange(index)}
                                                                    size="small"
                                                                    disabled={isPublished}
                                                                    sx={{
                                                                        color: theme.palette.primary.main,
                                                                        '&.Mui-checked': {
                                                                            color: theme.palette.primary.main,
                                                                        },
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    checked={defaultSelectedOption === index}
                                                                    onChange={() => handleDefaultSelectionChange(index)}
                                                                    size="small"
                                                                    disabled={isPublished}
                                                                    sx={{
                                                                        color: theme.palette.primary.main,
                                                                        '&.Mui-checked': {
                                                                            color: theme.palette.primary.main,
                                                                        },
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {options.length > 1 && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleRemoveOption(index)}
                                                                    disabled={isPublished}
                                                                    sx={{ 
                                                                        color: theme.palette.error.main,
                                                                        padding: '2px',
                                                                        minWidth: '20px',
                                                                        width: '20px',
                                                                        height: '20px',
                                                                        bgcolor: 'transparent',
                                                                        '&:hover': { bgcolor: theme.palette.action.hover }
                                                                    }}
                                                                >
                                                                    <Delete />
                                                                </IconButton>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Button
                                        startIcon={<Add />}
                                        onClick={handleAddOption}
                                        disabled={isPublished}
                                        sx={{
                                            alignSelf: 'flex-start',
                                            color: theme.palette.primary.main,
                                            bgcolor: theme.palette.background.paper,
                                            '&:hover': {
                                                bgcolor: theme.palette.action.hover,
                                            },
                                        }}
                                    >
                                        Add Option
                                    </Button>
                                </Stack>
                            </Box>
                        )}
                        {currentTab === 2 && (
                            <Box sx={{ p: 3, display: 'flex', gap: 3, height: `calc(100% - ${MEDIUM_GAP}px)` }}>
                                {/* Language list */}
                                <Box sx={{ minWidth: 300, maxHeight: '100%', overflowY: 'auto', border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, p: 0, bgcolor: theme.palette.background.paper }}>
                                    {languages.map(lang => {
                                        const isSelected = activeLanguage === lang.code;
                                        const changed = isTranslationChanged(lang.code);
                                        const accepted = !!(translations[lang.code]?.name || translations[lang.code]?.description);
                                        const isNotSupported = lang.translationProvider === null;
                                        
                                        const rowContent = (
                                            <Box 
                                                key={lang.code} 
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    px: 2, 
                                                    py: 1, 
                                                    borderBottom: `1px solid ${theme.palette.divider}`, 
                                                    bgcolor: isSelected ? theme.palette.action.selected : 'inherit', 
                                                    cursor: isNotSupported ? 'default' : 'pointer', 
                                                    opacity: isNotSupported ? 0.5 : 1,
                                                    '&:hover': { 
                                                        bgcolor: isNotSupported ? undefined : theme.palette.action.hover 
                                                    } 
                                                }} 
                                                onClick={() => !isNotSupported && setActiveLanguage(lang.code)}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, flex: 1 }}>{lang.name}</Typography>
                                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, flex: 1 }}>{lang.localeName}</Typography>
                                                <Checkbox
                                                    checked={accepted}
                                                    readOnly
                                                    size="small"
                                                    sx={{ p: 0, ml: 1 }}
                                                />
                                                {lang.code !== 'en' && changed && <Typography component="span" sx={{ color: theme.palette.primary.main, fontSize: '1.2rem', fontWeight: 600, ml: 1 }}>•</Typography>}
                                            </Box>
                                        );

                                        return isNotSupported ? (
                                            <Tooltip 
                                                key={lang.code}
                                                title="This language is not supported by our translation service. Translations for this language cannot be provided."
                                                arrow
                                            >
                                                <span style={{ display: 'block' }}>{rowContent}</span>
                                            </Tooltip>
                                        ) : rowContent;
                                    })}
                                </Box>

                                {/* Translation form */}
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }}>
                                            Language: {languages.find((l) => l.code === activeLanguage)?.name || activeLanguage}
                                        </Typography>
                                        {activeLanguage !== 'en' && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 4 }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: theme.palette.primary.main,
                                                        borderColor: theme.palette.primary.main,
                                                        '&:hover': {
                                                            borderColor: theme.palette.primary.dark,
                                                            color: theme.palette.primary.dark,
                                                        },
                                                    }}
                                                    disabled={isTranslating || isPublished}
                                                    onClick={handleTranslateAI}
                                                    endIcon={<img src={AIIcon} alt="AI" style={{ width: 20, height: 20 }} />}
                                                >
                                                    {isTranslating ? 'Translating...' : 'Translate using AI'}
                                                </Button>
                                                <Tooltip
                                                    title="AI-generated translations may contain errors or inaccuracies. Please review and cross-check the translation before accepting."
                                                    arrow
                                                >
                                                    <Box component="span">
                                                        <InfoOutlinedIcon 
                                                            fontSize="small" 
                                                            sx={{ 
                                                                color: theme.palette.neutral ? theme.palette.neutral.main : theme.palette.text.secondary, 
                                                                cursor: 'pointer',
                                                                fontWeight: 400
                                                            }} 
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </Box>
                                    <TextField
                                        label={name || "Name"}
                                        value={translations[activeLanguage]?.name || ''}
                                        onChange={e => handleTranslationFieldChange('name', e.target.value)}
                                        disabled={activeLanguage === 'en' || isPublished}
                                        fullWidth
                                        size="small"
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        label={description || "Description"}
                                        value={translations[activeLanguage]?.description || ''}
                                        onChange={e => handleTranslationFieldChange('description', e.target.value)}
                                        disabled={activeLanguage === 'en' || isPublished}
                                        fullWidth
                                        multiline
                                        rows={3}
                                    />
                                    {options.length > 0 && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle1" sx={{ mb: 2, color: theme.palette.text.primary }}>
                                                Option Translations
                                            </Typography>
                                            <Stack spacing={2}>
                                                {options.map((option, index) => (
                                                    <TextField
                                                        key={index}
                                                        label={option.optionValue}
                                                        value={translations[activeLanguage]?.options?.[index]?.value ?? option.optionValue}
                                                        onChange={e => handleOptionTranslationChange(index, e.target.value)}
                                                        disabled={activeLanguage === 'en' || isPublished}
                                                        fullWidth
                                                        size="small"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                bgcolor: theme.palette.background.paper,
                                                                '& fieldset': {
                                                                    borderColor: theme.palette.divider,
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: theme.palette.primary.main,
                                                                },
                                                            },
                                                            '& .MuiInputLabel-root': {
                                                                color: theme.palette.text.secondary,
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}
                                    {activeLanguage !== 'en' && (
                                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                            <Button 
                                                variant="contained" 
                                                onClick={handleSaveTranslation} 
                                                disabled={isSavingTranslation || !hasAnyTranslationChanged() || isPublished} 
                                                sx={{ 
                                                    fontWeight: 600, 
                                                    bgcolor: theme.palette.primary.main, 
                                                    color: theme.palette.primary.contrastText, 
                                                    '&:hover': { 
                                                        bgcolor: theme.palette.primary.dark 
                                                    } 
                                                }}
                                            >
                                                {isSavingTranslation ? <CircularProgress size={24} color="inherit" /> : 'Save'}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                    {/* Common footer for Details and Choices tabs */}
                    {(currentTab === 0 || currentTab === 1) && (
                        <Box sx={{
                            p: 2,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                            bgcolor: theme.palette.background.paper
                        }}>
                            <Button
                                onClick={handlePanelClose}
                                disabled={saving || isPublished}
                                sx={{ color: theme.palette.text.primary }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSaveDetailsChoices}
                                disabled={saving || !(hasDetailsChanged() || hasChoicesChanged()) || isPublished}
                                sx={{
                                    bgcolor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        bgcolor: theme.palette.primary.dark,
                                    },
                                    minWidth: 100,
                                    position: 'relative'
                                }}
                            >
                                {saving ? (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            color: theme.palette.primary.contrastText,
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            marginTop: '-12px',
                                            marginLeft: '-12px',
                                        }}
                                    />
                                ) : (
                                    'Save'
                                )}
                            </Button>
                        </Box>
                    )}
                </Box>

                <Dialog
                    open={showCloseConfirm}
                    onClose={handleCancelClose}
                    aria-labelledby="close-confirm-dialog-title"
                >
                    <DialogTitle id="close-confirm-dialog-title">
                        Unsaved Changes
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            You have unsaved changes. Are you sure you want to close without saving?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelClose} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmClose} color="error" autoFocus>
                            Close Without Saving
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar 
                    open={!!error} 
                    autoHideDuration={config.ui.snackbar.autoHideDuration}
                    onClose={() => setError(null)}
                    anchorOrigin={config.ui.snackbar.anchorOrigin}
                >
                    <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                </Snackbar>
                <Snackbar
                    open={saveSuccess}
                    autoHideDuration={3000}
                    onClose={() => setSaveSuccess(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
                        Preference updated successfully
                    </Alert>
                </Snackbar>
            </Box>
        </Modal>
    );
};

export default EditDPOptionPanel; 